import {
  sqliteTable,
  text,
  integer,
  real,
  primaryKey,
} from "drizzle-orm/sqlite-core";

// ─── Event log (append-only, source of truth) ────────────────────────

export const events = sqliteTable("events", {
  id: text("id").primaryKey(),
  type: text("type").notNull(),
  entityId: text("entity_id").notNull(),
  payload: text("payload", { mode: "json" }).notNull(),
  userId: text("user_id").notNull(),
  sessionId: text("session_id").notNull(),
  clientTimestamp: integer("client_timestamp").notNull(),
  serverTimestamp: integer("server_timestamp").notNull(),
});

// ─── Materialized views (derived from events) ────────────────────────

export const regions = sqliteTable("regions", {
  id: text("id").primaryKey(),
  name: text("name"),
  archived: integer("archived").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

export const hospitals = sqliteTable("hospitals", {
  id: text("id").primaryKey(),
  name: text("name"),
  regionId: text("region_id"),
  mergedInto: text("merged_into"),
  archived: integer("archived").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

export const units = sqliteTable("units", {
  id: text("id").primaryKey(),
  name: text("name"),
  mergedInto: text("merged_into"),
  archived: integer("archived").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

export const users = sqliteTable("users", {
  id: text("id").primaryKey(),
  email: text("email").notNull().unique(),
  name: text("name"),
  picture: text("picture"),
  role: text("role").notNull().default("unauthorized"),
  firstLoginAt: integer("first_login_at"),
  lastLoginAt: integer("last_login_at"),
  inviteSentAt: integer("invite_sent_at"),
  updatedAt: integer("updated_at").notNull(),
});

export const userHospitals = sqliteTable(
  "user_hospitals",
  {
    userId: text("user_id").notNull(),
    hospitalId: text("hospital_id").notNull(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.userId, t.hospitalId] }),
  }),
);

export const userUnits = sqliteTable(
  "user_units",
  {
    userId: text("user_id").notNull(),
    unitId: text("unit_id").notNull(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.userId, t.unitId] }),
  }),
);

export const userRegions = sqliteTable(
  "user_regions",
  {
    userId: text("user_id").notNull(),
    regionId: text("region_id").notNull(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.userId, t.regionId] }),
  }),
);

export const unitHierarchy = sqliteTable(
  "unit_hierarchy",
  {
    parentUnitId: text("parent_unit_id").notNull(),
    childUnitId: text("child_unit_id").notNull(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.parentUnitId, t.childUnitId] }),
  }),
);

export const sessions = sqliteTable("sessions", {
  id: text("id").primaryKey(),
  userId: text("user_id").notNull(),
  destroyed: integer("destroyed").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

export const patients = sqliteTable("patients", {
  id: text("id").primaryKey(),
  unitId: text("unit_id"),
  mergedInto: text("merged_into"),
  idNumber: text("id_number"), // PDF field: особистий номер
  name: text("name"),
  dateOfBirth: text("date_of_birth"),
  sex: text("sex"),
  bloodType: text("blood_type"),
  rhFactor: text("rh_factor"),
  allergyStatus: text("allergy_status"),
  allergyDetails: text("allergy_details"),
  militaryRank: text("military_rank"),
  lastUsedWatch: text("last_used_watch"), // FICR UID of most recently paired watch

  patientCategory: text("patient_category"),
  personalDataErased: integer("personal_data_erased").notNull().default(0),
  archived: integer("archived").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

// ─── Field events (operational periods) ──────────────────────────────

export const fieldEvents = sqliteTable("field_events", {
  id: text("id").primaryKey(),
  unitId: text("unit_id").notNull(),
  leadMedicId: text("lead_medic_id").notNull(),
  medics: text("medics", { mode: "json" }).$type<string[]>().notNull().default([]),
  combatants: text("combatants", { mode: "json" }).$type<string[]>().notNull().default([]),
  startedAt: integer("started_at").notNull(),
  stoppedAt: integer("stopped_at"),
  updatedAt: integer("updated_at").notNull(),
});

// ─── Triage assignments ───────────────────────────────────────────────

export const fieldEventAssignments = sqliteTable("field_event_assignments", {
  id: text("id").primaryKey(),           // command id for idempotency
  fieldEventId: text("field_event_id").notNull(),
  watchId: text("watch_id").notNull(),
  medicId: text("medic_id").notNull(),
  assignedAt: integer("assigned_at").notNull(),
  completedAt: integer("completed_at"),  // null = still active
});

// ─── Watch event log ──────────────────────────────────────────────────

export const watchEventLog = sqliteTable("watch_event_log", {
  id: text("id").primaryKey(),
  fieldEventId: text("field_event_id").notNull(),
  soldierId: text("soldier_id"),
  watchId: text("watch_id").notNull(),
  type: text("type").notNull(), // PAIR | UNPAIR | TRIAGE_ALERT | MAN_DOWN
  gpsLat: real("gps_lat"),
  gpsLon: real("gps_lon"),
  gpsAccuracyM: real("gps_accuracy_m"),
  payload: text("payload", { mode: "json" }).notNull().default({}),
  timestamp: integer("timestamp").notNull(),
  receivedAt: integer("received_at").notNull(),
});

export const cases = sqliteTable("cases", {
  id: text("id").primaryKey(),
  patientId: text("patient_id").notNull(),
  fieldEventId: text("field_event_id"),
  unitId: text("unit_id"),
  number: text("number"),
  hospitalId: text("hospital_id"),
  eventTime: integer("event_time"),
  eventLocation: text("event_location"),
  arrivalTime: integer("arrival_time"),
  triageCategory: text("triage_category"),
  role: text("role"),
  medicalFacility: text("medical_facility"),
  transportType: text("transport_type", { mode: "json" }).$type<string[]>(),
  traumaTypes: text("trauma_types", { mode: "json" }).$type<string[]>(),
  traumaPenetrating: integer("trauma_penetrating"),
  traumaNonPenetrating: integer("trauma_non_penetrating"),
  traumaLocations: text("trauma_locations", { mode: "json" }).$type<{
    burns: string[];
    traumas: string[];
    injuries: Array<{ id: string; x: number; y: number }>;
    arrows: Array<{ from: string; to: string }>;
  }>(),
  conditionTypes: text("condition_types", { mode: "json" }).$type<string[]>(),
  cbrn: text("cbrn", { mode: "json" }).$type<string[]>(),
  priorCare: text("prior_care", { mode: "json" }).$type<
    Record<string, boolean | number | string>
  >(),
  priorCareTime: integer("prior_care_time"),
  tetanusProphylaxis: text("tetanus_prophylaxis"),
  // Role 1 care (structured checkboxes from PDF section 5)
  r1Care: text("r1_care", { mode: "json" }).$type<
    Record<string, boolean | string>
  >(),
  r1Kit1: text("r1_kit1"),
  r1Kit2: text("r1_kit2"),
  deathTime: integer("death_time"),
  deathCircumstances: text("death_circumstances"),
  // Discharge — Role 1
  dischargeTime: integer("discharge_time"),
  dischargeDestination: text("discharge_destination"),
  dischargeHospitalId: text("discharge_hospital_id"),
  dischargeUnitId: text("discharge_unit_id"),
  evacuationCategory: text("evacuation_category"),
  dischargeTransportType: text("discharge_transport_type", { mode: "json" }).$type<string[]>(),
  dischargePosition: text("discharge_position"),
  escortDoctor: integer("escort_doctor"),
  escortNurse: integer("escort_nurse"),
  dischargeRole: text("discharge_role"),
  dischargeVehicleType: text("discharge_vehicle_type"),
  dischargeEquipment: text("discharge_equipment", { mode: "json" }).$type<
    Record<string, unknown>
  >(),
  diagnosis: text("diagnosis"),
  notes: text("notes"),
  medicName: text("medic_name"),
  medicFacility: text("medic_facility"),
  medicRole: text("medic_role"),
  // Role 2 fields (page 2 of PDF form)
  r2ArrivalTime: integer("r2_arrival_time"),
  r2TriageCategory: text("r2_triage_category"),
  r2HospitalId: text("r2_hospital_id"),
  r2Care: text("r2_care", { mode: "json" }).$type<
    Record<string, boolean | string>
  >(),
  r2DeathTime: integer("r2_death_time"),
  r2DeathCircumstances: text("r2_death_circumstances"),
  r2DischargeTime: integer("r2_discharge_time"),
  r2DischargeDestination: text("r2_discharge_destination"),
  r2EvacuationCategory: text("r2_evacuation_category"),
  r2DischargeTransportType: text("r2_discharge_transport_type", { mode: "json" }).$type<string[]>(),
  r2DischargePosition: text("r2_discharge_position"),
  r2EscortDoctor: integer("r2_escort_doctor"),
  r2EscortNurse: integer("r2_escort_nurse"),
  r2DischargeRole: text("r2_discharge_role"),
  r2DischargeVehicleType: text("r2_discharge_vehicle_type"),
  r2DischargeEquipment: text("r2_discharge_equipment", { mode: "json" }).$type<
    Record<string, unknown>
  >(),
  r1PriorCareStatus: text("r1_prior_care_status"),
  r2TetanusProphylaxis: text("r2_tetanus_prophylaxis"),
  r2Kit1: text("r2_kit1"),
  r2Kit2: text("r2_kit2"),
  r2Diagnosis: text("r2_diagnosis"),
  r2Notes: text("r2_notes"),
  r2MedicName: text("r2_medic_name"),
  r2MedicFacility: text("r2_medic_facility"),
  r2MedicRole: text("r2_medic_role"),
  signatureSha256: text("signature_sha256"), // R1 medic signature (blob SHA256)
  r2SignatureSha256: text("r2_signature_sha256"), // R2 medic signature (blob SHA256)
  createdBy: text("created_by"),
  archived: integer("archived").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

// ─── Time-series child tables (all have `role` for R1/R2 scoping) ────

export const caseVitals = sqliteTable("case_vitals", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"), // 1 or 2
  time: integer("time"),
  respiratoryRate: integer("respiratory_rate"),
  spO2: integer("sp_o2"),
  pulse: integer("pulse"),
  systolic: integer("systolic"),
  diastolic: integer("diastolic"),
  gcsEye: integer("gcs_eye"),
  gcsVerbal: integer("gcs_verbal"),
  gcsMotor: integer("gcs_motor"),
  painScale: integer("pain_scale"),
  temperature: text("temperature"),
  diuresis: integer("diuresis"),
  updatedAt: integer("updated_at").notNull(),
});

export const caseVentilation = sqliteTable("case_ventilation", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  time: integer("time"),
  mode: text("mode"),
  rate: integer("rate"),
  tidalVolume: integer("tidal_volume"),
  fiO2: integer("fi_o2"),
  peep: integer("peep"),
  pip: integer("pip"),
  etCO2: integer("et_co2"),
  updatedAt: integer("updated_at").notNull(),
});

export const caseDiagnostics = sqliteTable("case_diagnostics", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  time: integer("time"),
  eFast: text("e_fast"),
  xray: text("xray"),
  ct: text("ct"),
  ecg: text("ecg"),
  ultrasound: text("ultrasound"),
  otoscopyLeft: text("otoscopy_left"),
  otoscopyRight: text("otoscopy_right"),
  pupilsLeft: text("pupils_left"),
  pupilsRight: text("pupils_right"),
  other: text("other"),
  notes: text("notes"),
  updatedAt: integer("updated_at").notNull(),
});

export const caseLabValues = sqliteTable("case_lab_values", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  time: integer("time"),
  hg: text("hg"),
  hct: text("hct"),
  rbc: text("rbc"),
  wbc: text("wbc"),
  platelets: text("platelets"),
  na: text("na"),
  k: text("k"),
  ca: text("ca"),
  cl: text("cl"),
  lactate: text("lactate"),
  urea: text("urea"),
  creatinine: text("creatinine"),
  glucose: text("glucose"),
  ph: text("ph"),
  pCO2: real("p_co2"),
  pO2: real("p_o2"),
  hCO3: text("h_co3"),
  notes: text("notes"),
  updatedAt: integer("updated_at").notNull(),
});

// Exam — all fields from PDF form
export const caseExam = sqliteTable("case_exam", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  time: integer("time"),
  airwayStatus: text("airway_status"),
  advancedAirwayType: text("advanced_airway_type"),
  respiratorySupportType: text("respiratory_support_type"),
  breathingSounds: text("breathing_sounds", { mode: "json" }).$type<
    Record<string, boolean>
  >(),
  skinColor: text("skin_color"),
  skinMoisture: integer("skin_moisture"),
  skinTemperature: text("skin_temperature"),
  skinEdema: integer("skin_edema"),
  skinRash: integer("skin_rash"),
  skinPink: integer("skin_pink"),
  skinPale: integer("skin_pale"),
  skinCyanotic: integer("skin_cyanotic"),
  skinDry: integer("skin_dry"),
  skinMoist: integer("skin_moist"),
  chestSymmetric: integer("chest_symmetric"),
  chestAsymRight: integer("chest_asym_right"),
  chestAsymLeft: integer("chest_asym_left"),
  chestFlailLeft: integer("chest_flail_left"),
  chestFlailRight: integer("chest_flail_right"),
  chestPenetratingLeft: integer("chest_penetrating_left"),
  chestPenetratingRight: integer("chest_penetrating_right"),
  heartSoundsClear: integer("heart_sounds_clear"),
  tracheaMidline: integer("trachea_midline"),
  tracheaDeviationRight: integer("trachea_deviation_right"),
  tracheaDeviationLeft: integer("trachea_deviation_left"),
  peripheralPulse: text("peripheral_pulse"),
  capillaryRefillOver2s: integer("capillary_refill_over_2s"),
  jugularVeinsCollapsed: integer("jugular_veins_collapsed"),
  jugularVeinsDistended: integer("jugular_veins_distended"),
  abdomenDistended: integer("abdomen_distended"),
  abdomenPainful: integer("abdomen_painful"),
  peritonealIrritation: integer("peritoneal_irritation"),
  abdomenPenetratingTrauma: integer("abdomen_penetrating_trauma"),
  pelvisStable: integer("pelvis_stable"),
  limbDeformation: text("limb_deformation", { mode: "json" }).$type<
    Record<string, boolean>
  >(),
  neuroDeficit: text("neuro_deficit", { mode: "json" }).$type<
    Record<string, boolean>
  >(),
  deformationAny: integer("deformation_any"),
  neuroDeficitAny: integer("neuro_deficit_any"),
  pupilsEqual: text("pupils_equal"),
  pupilsLightReaction: integer("pupils_light_reaction"),
  pupilsLightReactionLeft: integer("pupils_light_reaction_left"),
  pupilsLightReactionRight: integer("pupils_light_reaction_right"),
  visionLeft: text("vision_left"),
  visionRight: text("vision_right"),
  skullFracture: integer("skull_fracture"),
  csfLeak: integer("csf_leak"),
  cervicalSpineInjury: integer("cervical_spine_injury"),
  gcsTotal: integer("gcs_total"),
  updatedAt: integer("updated_at").notNull(),
});

export const caseCpr = sqliteTable("case_cpr", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  startTime: integer("start_time"),
  endTime: integer("end_time"),
  updatedAt: integer("updated_at").notNull(),
});

// Tourniquet — replaces free-text treatment for structured form data
export const caseTourniquet = sqliteTable("case_tourniquet", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  limb: text("limb"), // rue | lue | rle | lle
  applicationTime: integer("application_time"),
  removalTime: integer("removal_time"),
  conversionTime: integer("conversion_time"),
  repositioningTime: integer("repositioning_time"),
  updatedAt: integer("updated_at").notNull(),
});

export const caseMedication = sqliteTable("case_medication", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  time: integer("time"),
  name: text("name"),
  dose: text("dose"),
  doseUnit: text("dose_unit"),
  route: text("route"),
  updatedAt: integer("updated_at").notNull(),
});

export const caseSurgery = sqliteTable("case_surgery", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  role: integer("role"),
  startTime: integer("start_time"),
  endTime: integer("end_time"),
  surgeryTypes: text("surgery_types", { mode: "json" }).$type<string[]>(),
  description: text("description"),
  anesthesiaLocal: integer("anesthesia_local"),
  anesthesiaRegional: integer("anesthesia_regional"),
  anesthesiaGeneral: integer("anesthesia_general"),
  anesthesiaSedation: integer("anesthesia_sedation"),
  updatedAt: integer("updated_at").notNull(),
});

// ─── Attachments ──────────────────────────────────────────────────────

export const attachments = sqliteTable("attachments", {
  id: text("id").primaryKey(),
  caseId: text("case_id").notNull(),
  mimeType: text("mime_type"),
  capturedAt: integer("captured_at"),
  sha256: text("sha256"),
  caption: text("caption"),
  deleted: integer("deleted").notNull().default(0),
  updatedAt: integer("updated_at").notNull(),
});

// ─── Client-only: pending commands queue ──────────────────────────────

export const pendingCommands = sqliteTable("pending_commands", {
  id: text("id").primaryKey(),
  sessionId: text("session_id").notNull(),
  envelope: text("envelope", { mode: "json" }).notNull(),
  createdAt: integer("created_at").notNull(),
});
